import React, { useState, useEffect, useContext, useCallback, useRef} from "react";
import ActionsContext from "./ActionsContext";
import Loading from "common/Loading";
import useWait from "common/hooks/useWait";
import { RELOAD_PERIOD } from "common/constants";

const canFail = (provider, max=3) => {
  let failed = 0;

  const letsTry = (...args) => provider(...args).then(resetAndContinue).catch(retryOrFail);
  const resetAndContinue = (result) => {failed = 0; return result};
  const retryOrFail = (error) => {
    failed += 1;
    if (failed >= max) {
      throw error;
    }
    console.error(error)
    return letsTry();
  }
  return letsTry;
} 

const withReloading = (Component) => {
  const WithReloading = ({
    provider,
    every = RELOAD_PERIOD,
    signal = 'do-load',
    ...rest
  }) => {
    const actions = useContext(ActionsContext);
    const [outcome, setOutcome] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [wait, stop] = useWait(every);
    const mountedRef = useRef(true);
    // Set on stop and cleared when a load starts, so results of requests
    // in flight when stop was called are discarded, while explicit loads
    // issued during a pause apply and resume the reloading chain.
    const stoppedRef = useRef(false);
    // Reloading pauses while a modal is open, the settings form has
    // pending changes or table items are selected.
    const modalRef = useRef(false);
    const dirtyRef = useRef(false);
    const selectionRef = useRef(false);
    const providerRef = useRef(canFail(provider, 3/*times*/));
    useEffect(() => {
      providerRef.current = canFail(provider, 3/*times*/);
    }, [provider]);

    const hasPauseCause = () =>
      modalRef.current || dirtyRef.current || selectionRef.current;

    const maySetOutcome = (data) => {
      if (!mountedRef.current || stoppedRef.current) return;
      setLoadError(null);
      setOutcome(data);
    };

    // Fire and forget: returning the wait chain would link every cycle
    // to the next one, retaining the whole history of pending promises.
    const reSchedule = () => {
      if (!mountedRef.current || stoppedRef.current || hasPauseCause()) {
        return;
      }
      wait().then((elapsed) => elapsed && load());
    };

    const reportError = (error) => {
      console.error(error);
      if (mountedRef.current && !stoppedRef.current) {
        setLoadError(error);
      }
    };

    const load = useCallback(() => {
      stoppedRef.current = false;
      stop();
      return providerRef.current()
        .then(maySetOutcome)
        .then(reSchedule)
        .catch(reportError);
    }, []);

    const stopReloading = useCallback(() => {
      stoppedRef.current = true;
      stop();
    }, []);

    const mayStartReloading = useCallback(() => {
      if (!mountedRef.current || hasPauseCause()) return;
      stoppedRef.current = false;
      reSchedule();
    }, []);

    useEffect(() => {
      mountedRef.current = true;
      load();
      return () => { mountedRef.current = false; stop(); };
    }, []);

    useEffect(() => {
      if (signal !== null) {
        return actions.recv(signal, load);
      }
    }, [signal, load]);

    useEffect(() => actions.recv('open-modal', () => {
      modalRef.current = true;
      stopReloading();
    }), []);

    useEffect(() => actions.recv('close-modal', () => {
      modalRef.current = false;
      mayStartReloading();
    }), []);

    useEffect(() => actions.recv('state-changed', ({ pristine }) => {
      if (modalRef.current) return;

      const dirty = pristine !== true;
      if (dirty === dirtyRef.current) return;
      dirtyRef.current = dirty;
      if (dirty) {
        stopReloading();
      } else {
        mayStartReloading();
      }
    }), []);

    useEffect(() => actions.recv('selection-changed', ({ count }) => {
      const hasSelection = count > 0;
      if (hasSelection === selectionRef.current) return;
      selectionRef.current = hasSelection;
      if (hasSelection) {
        stopReloading();
      } else {
        mayStartReloading();
      }
    }), []);

    return outcome === null
      ? loadError !== null
        ? <div className="error align-left">{loadError.message || String(loadError)}</div>
        : <Loading />
      : <Component {...rest} outcome={outcome} />;
  };
  WithReloading.displayName = `WithReloading(${Component.displayName ?? Component.name})`;
  return WithReloading;
};

export default withReloading;
